import SimpleBar from 'simplebar-react';
import { alpha, styled } from '@mui/material/styles';

/**
 * Root wrapper for the scrollbar container. Provides a full-height, flex-growing
 * container with hidden overflow so the inner SimpleBar can manage scrolling.
 *
 * Accepts standard div props: `children`, `className`, `style`, `ref`, and
 * other HTML div attributes.
 *
 * @example
 * <StyledRootScrollbar>
 *   <StyledScrollbar sx={{ maxHeight: 300 }}>
 *     <div>Scrollable content</div>
 *   </StyledScrollbar>
 * </StyledRootScrollbar>
 */
export const StyledRootScrollbar = styled('div')(() => ({
  flexGrow: 1,
  height: '100%',
  overflow: 'hidden',
}));

/**
 * Theme-styled SimpleBar wrapper. Renders custom scrollbar track/thumb using
 * theme palette (midGray) and fixed track dimensions. Wraps simplebar-react
 * SimpleBar and accepts its props (e.g. `children`, `scrollableNodeProps`,
 * `className`, `style`) plus MUI styled props such as `sx`.
 *
 * Use inside StyledRootScrollbar for desktop scroll regions. Accepts
 * simplebar-react Props and MUI `sx` for layout (e.g. maxHeight).
 *
 * @example
 * <StyledScrollbar sx={{ maxHeight: '100%' }}>
 *   <div>Long content here</div>
 * </StyledScrollbar>
 */
export const StyledScrollbar = styled(SimpleBar)(({ theme }) => ({
  maxHeight: '100%',
  '& .simplebar-scrollbar': {
    '&:before': {
      backgroundColor: alpha(theme.palette.common.midGray, 0.48),
    },
    '&.simplebar-visible:before': {
      opacity: 1,
    },
  },
  '& .simplebar-track.simplebar-vertical': {
    width: 10,
  },
  '& .simplebar-track.simplebar-horizontal .simplebar-scrollbar': {
    height: 6,
  },
  '& .simplebar-mask': {
    zIndex: 'inherit',
  },
  '& .simplebar-content-wrapper': {
    height: '100% !important',
  },
}));
